import { Alert, Badge, Button, DataTable, dialogs, Drawer, EmptyState, Field, PageHeader, Pagination, SegmentedControl, Skeleton, Textarea, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, LifeBuoy, Send } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api, dt, errorText, http, useCan } from "../lib/api";

type Ticket = {
  id: string; companyId: string; companyName?: string | null; number: number; subject: string; category: string; status: "open" | "answered" | "closed";
  priority: "normal" | "high"; slaDueAt: string | null; lastMessageAt: string; createdAt: string; overdue: boolean;
};
type Full = Ticket & {
  messages: { id: string; authorType: "user" | "platform"; authorName: string; body: string; createdAt: string }[];
  company: { id: string; name: string } | null; author: { name: string; email: string | null; phone: string | null } | null;
};

const STATUS: Record<Ticket["status"], { label: string; tone: "warning" | "success" | "neutral" }> = {
  open: { label: "Ждёт ответа", tone: "warning" }, answered: { label: "Ответили, ждём клиента", tone: "success" }, closed: { label: "Решено", tone: "neutral" },
};
const CATEGORY: Record<string, string> = { question: "Вопрос", problem: "Что-то не работает", billing: "Оплата и подписка", idea: "Предложение" };

/** Сколько осталось до срока ответа: «через 1 ч 20 мин» / «просрочено на 3 ч». */
function due(t: Pick<Ticket, "status" | "slaDueAt">) {
  if (t.status !== "open" || !t.slaDueAt) return null;
  const min = Math.round((new Date(t.slaDueAt).getTime() - Date.now()) / 60_000);
  const fmt = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} ч${m % 60 ? ` ${m % 60} мин` : ""}` : `${m} мин`);
  return min < 0 ? { late: true, text: `просрочено на ${fmt(-min)}` } : { late: false, text: `ответить через ${fmt(min)}` };
}

function TicketDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["support", id], queryFn: () => http.get<Full>(`/support/tickets/${id}`) });
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const done = (r: Full) => {
    qc.setQueryData(["support", id], r);
    void qc.invalidateQueries({ queryKey: ["support-list"] });
    void qc.invalidateQueries({ queryKey: ["support-summary"] });
  };
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      done(await http.post<Full>(`/support/tickets/${id}/messages`, { body }));
      setBody("");
      toast.success("Ответ отправлен — клиент получит уведомление");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const close = () => void dialogs.confirm({
    title: "Закрыть обращение?", description: "Клиент сможет написать в нём снова — тогда оно откроется.", confirmText: "Закрыть",
    onConfirm: async () => {
      try {
        done(await http.post<Full>(`/support/tickets/${id}/close`));
      } catch (e) {
        toast.error(errorText(e));
      }
    },
  });
  const t = q.data;
  const d = t && due(t);
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} width="lg" title={t ? `№ ${t.number} · ${t.subject}` : "…"} description={t ? CATEGORY[t.category] ?? t.category : undefined}
      footer={t && can("support.reply") && (
        <div className="flex w-full flex-col gap-3">
          <Field label="Ответ клиенту"><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={5000} placeholder="Простыми словами, без кодов и терминов" /></Field>
          <div className="flex flex-wrap gap-2">
            <Button leftIcon={<Send />} loading={busy} disabled={!body.trim()} onClick={() => void send()}>Ответить</Button>
            {t.status !== "closed" && <Button variant="outline" leftIcon={<CheckCircle2 />} onClick={close}>Закрыть</Button>}
          </div>
        </div>
      )}>
      {q.isError && <Alert tone="danger">{errorText(q.error)}</Alert>}
      {q.isPending && <Skeleton className="h-64" />}
      {t && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={t.overdue ? "danger" : STATUS[t.status].tone} dot>{STATUS[t.status].label}</Badge>
            {t.priority === "high" && <Badge tone="primary">Быстрая поддержка</Badge>}
            {d && <span className={`text-sm ${d.late ? "text-danger" : "text-muted"}`}>{d.text}</span>}
          </div>
          <div className="rounded-tile bg-surface-2 px-4 py-3 text-sm">
            {t.company && <p><Link className="font-medium text-primary hover:underline" to={`/companies/${t.company.id}`}>{t.company.name}</Link></p>}
            {t.author && <p className="text-muted">{[t.author.name, t.author.email, t.author.phone].filter(Boolean).join(" · ")}</p>}
          </div>
          <ol className="flex flex-col gap-3">
            {t.messages.map((m) => {
              const ours = m.authorType === "platform";
              return (
                <li key={m.id} className={ours ? "ms-8 self-end" : "me-8 self-start"}>
                  <div className={`rounded-tile px-4 py-3 ${ours ? "bg-primary-soft" : "bg-surface-2"}`}><p className="whitespace-pre-wrap break-words text-sm">{m.body}</p></div>
                  <p className={`mt-1 text-xs text-subtle ${ours ? "text-end" : ""}`}>{m.authorName} · {dt(m.createdAt)}</p>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </Drawer>
  );
}

/** Обращения клиентов: очередь — сначала ждущие ответа с «Быстрой поддержкой», дальше по сроку ответа. */
export default function SupportPage() {
  const [status, setStatus] = useState<"open" | "answered" | "closed" | "all">("open");
  const [page, setPage] = useState(1);
  // Открытое обращение — в адресе: на него ведёт тост о новом сообщении
  const [params, setParams] = useSearchParams();
  const open = params.get("ticket");
  const setOpen = (id: string | null) => setParams(id ? { ticket: id } : {}, { replace: true });
  const summary = useQuery({ queryKey: ["support-summary"], queryFn: () => http.get<{ open: number; high: number; overdue: number }>("/support/summary"), refetchInterval: 60_000 });
  const list = useQuery({
    queryKey: ["support-list", status, page], refetchInterval: 60_000,
    queryFn: () => api<{ items: Ticket[]; meta: { total: number } }>("GET", "/support/tickets", undefined, { withMeta: true, query: { status: status === "all" ? undefined : status, page, pageSize: 50 } }),
  });
  const s = summary.data;
  return (
    <>
      <PageHeader title="Обращения" description="Вопросы и проблемы клиентов. С «Быстрой поддержкой» отвечаем за 2 часа, без неё — за 24 часа." />
      {s && s.overdue > 0 && <Alert tone="danger" title={`Просрочено: ${s.overdue}`}>Клиенты ждут дольше обещанного — ответьте им первыми.</Alert>}
      {s && <p className="text-sm text-muted">Ждут ответа: {s.open}, из них с быстрой поддержкой: {s.high}</p>}
      <SegmentedControl aria-label="Статус" value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}
        options={[{ value: "open" as const, label: "Ждут ответа" }, { value: "answered" as const, label: "Ответили" }, { value: "closed" as const, label: "Решены" }, { value: "all" as const, label: "Все" }]} />
      <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(t) => t.id} onRowClick={(t) => setOpen(t.id)} empty={<EmptyState icon={<LifeBuoy />} title="Обращений нет" />}
        columns={[
          { key: "s", header: "Тема", mobile: "title", cell: (t) => <span className="font-medium">№ {t.number} · {t.subject}</span> },
          { key: "c", header: "Компания", mobile: "subtitle", cell: (t) => t.companyName ?? "—" },
          { key: "k", header: "Тип", cell: (t) => CATEGORY[t.category] ?? t.category },
          { key: "p", header: "Приоритет", cell: (t) => (t.priority === "high" ? <Badge tone="primary">Быстрая</Badge> : <span className="text-muted">Обычный</span>) },
          { key: "d", header: "Срок ответа", cell: (t) => { const d = due(t); return d ? <span className={d.late ? "font-medium text-danger" : ""}>{d.text}</span> : "—"; } },
          { key: "u", header: "Последнее сообщение", cell: (t) => dt(t.lastMessageAt) },
          { key: "st", header: "Статус", cell: (t) => <Badge tone={t.overdue ? "danger" : STATUS[t.status].tone} dot>{STATUS[t.status].label}</Badge> },
        ]} />
      {list.data && <Pagination page={page} pageSize={50} total={list.data.meta.total} onPageChange={setPage} />}
      {open && <TicketDrawer key={open} id={open} onClose={() => setOpen(null)} />}
    </>
  );
}
